/**
 * RichTilePreview — realistic miniature screenshots for the Add Section
 * modal (Iter 144 redesign).
 *
 * Replaces the older schematic TilePreview (which drew pastel boxes +
 * grey lines and looked wireframe-y). Each preview now ships with REAL
 * stock photography, a real headline, real body copy, and visible CTA /
 * cards / form elements so vendors can SEE what they'll get within ~2
 * seconds of opening the picker.
 *
 * The categoryId + first block.type are used to pick the right "shape":
 *   • Hero / Intro       → background image + headline + sub + CTA
 *   • Services           → 3-icon card row
 *   • Pricing            → 3-tier card row
 *   • Testimonials       → portrait + quote + stars
 *   • Portfolio / Gallery → 4-photo grid
 *   • Team               → 3-portrait row
 *   • Contact            → form sketch + map dot
 *   • Donations          → impact image + form + stat row
 *   • FAQ                → accordion rows
 *   • Footer             → 4-column links sketch
 *   • Agency (long-scroll) → mini hero + service cards
 *
 * Pure presentational — no editing logic.
 */
import React from 'react';

const STOCK = {
  hero:    'https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=900&q=70',
  hero2:   'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=900&q=70',
  about:   'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&q=70',
  team1:   'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&q=70',
  team2:   'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&q=70',
  team3:   'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&q=70',
  port1:   'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&q=70',
  port2:   'https://images.unsplash.com/photo-1467043153537-a4fba2cd39ef?w=400&q=70',
  port3:   'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=400&q=70',
  port4:   'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&q=70',
  service: 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=900&q=70',
  donate:  'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=900&q=70',
  food:    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=900&q=70',
  testim:  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&q=70',
};

// ──────────────────────────────────────────────────────────────────────
// Mini sub-components — each renders at 100% of its container so they
// scale into whatever the tile-card aspect ratio gives them.
// ──────────────────────────────────────────────────────────────────────
const Pill = ({ children, color = '#0E0E23' }) => (
  <span style={{
    display: 'inline-block',
    background: color, color: '#fff',
    fontSize: 7, fontWeight: 700, letterSpacing: 0.6,
    padding: '3px 8px', borderRadius: 999, textTransform: 'uppercase',
  }}>{children}</span>
);

const Line = ({ w = '60%', h = 4, opacity = 0.85, mt = 4, color = '#fff' }) => (
  <div style={{ width: w, height: h, background: color, opacity, borderRadius: 2, marginTop: mt }} />
);

const Stars = () => (
  <div style={{ display: 'flex', gap: 1.5 }}>
    {[0,1,2,3,4].map(i => (
      <svg key={i} width={9} height={9} viewBox="0 0 12 12"><path d="M6 0l1.7 4 4.3.3-3.3 2.7 1 4.2L6 9l-3.7 2.2 1-4.2L0 4.3l4.3-.3z" fill="#FBBF24"/></svg>
    ))}
  </div>
);

// ──────────────────────────────────────────────────────────────────────
// SHAPES — one component per visual archetype.
// ──────────────────────────────────────────────────────────────────────
function HeroPreview({ img = STOCK.hero, headline = 'Welcome to our story', sub = 'Crafted with care', cta = 'Get Started' }) {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)' }} />
      <div style={{ position: 'absolute', left: 16, bottom: 18, right: 16, color: '#fff' }}>
        <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1, letterSpacing: -0.4 }}>{headline}</div>
        <div style={{ fontSize: 9, marginTop: 6, opacity: 0.85 }}>{sub}</div>
        <div style={{ marginTop: 10, display: 'inline-block', background: '#8B5CF6', color: '#fff', fontSize: 8, fontWeight: 700, padding: '5px 11px', borderRadius: 999 }}>{cta} →</div>
      </div>
    </div>
  );
}

function SplitImageText({ img = STOCK.about, headline = 'About our craft', sub = 'A short story of how we got here.' }) {
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', background: '#fff' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      <div style={{ flex: 1, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: 7, color: '#8B5CF6', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>About Us</div>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#0E0E23', lineHeight: 1.15, marginTop: 4 }}>{headline}</div>
        <div style={{ fontSize: 8, color: '#475569', lineHeight: 1.4, marginTop: 6 }}>{sub}</div>
        <Line w="50%" h={3} color="#CBD5E1" opacity={1} mt={6} />
        <Line w="35%" h={3} color="#CBD5E1" opacity={1} mt={4} />
      </div>
    </div>
  );
}

function ThreeCardRow({ title = 'What we do', cards = [{i: '⚡', t: 'Fast'}, {i: '🛡️', t: 'Trusted'}, {i: '💎', t: 'Premium'}], bg = '#F8FAFC' }) {
  return (
    <div style={{ background: bg, padding: 14, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#0E0E23', textAlign: 'center' }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, flex: 1 }}>
        {cards.map((c, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 8, padding: 8, boxShadow: '0 1px 3px rgba(15,23,42,0.07)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 16 }}>{c.i}</div>
            <div style={{ fontSize: 7, fontWeight: 800, color: '#0E0E23', marginTop: 4 }}>{c.t}</div>
            <Line w="60%" h={2} color="#CBD5E1" opacity={1} mt={3} />
            <Line w="45%" h={2} color="#CBD5E1" opacity={1} mt={2} />
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingPreview() {
  const tiers = [
    { name: 'Starter', price: '$9',  hi: false },
    { name: 'Pro',     price: '$29', hi: true  },
    { name: 'Team',    price: '$79', hi: false },
  ];
  return (
    <div style={{ background: '#F8FAFC', padding: 14, width: '100%', height: '100%' }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: '#0E0E23', textAlign: 'center', marginBottom: 10 }}>Simple pricing</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, alignItems: 'end' }}>
        {tiers.map((t, i) => (
          <div key={i} style={{ background: t.hi ? '#0E0E23' : '#fff', color: t.hi ? '#fff' : '#0E0E23', borderRadius: 8, padding: 9, boxShadow: '0 2px 6px rgba(15,23,42,0.08)' }}>
            <div style={{ fontSize: 6, opacity: 0.7, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>{t.name}</div>
            <div style={{ fontSize: 14, fontWeight: 900, marginTop: 2 }}>{t.price}<span style={{ fontSize: 7, opacity: 0.6 }}>/mo</span></div>
            <Line w="80%" h={2} color={t.hi ? '#fff' : '#CBD5E1'} opacity={t.hi ? 0.4 : 1} mt={6} />
            <Line w="70%" h={2} color={t.hi ? '#fff' : '#CBD5E1'} opacity={t.hi ? 0.4 : 1} mt={3} />
            <Line w="60%" h={2} color={t.hi ? '#fff' : '#CBD5E1'} opacity={t.hi ? 0.4 : 1} mt={3} />
            <div style={{ marginTop: 8, background: t.hi ? '#8B5CF6' : '#0E0E23', color: '#fff', fontSize: 7, fontWeight: 700, textAlign: 'center', padding: '4px 0', borderRadius: 4 }}>Choose</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsPreview() {
  return (
    <div style={{ background: '#FAF6EE', padding: 14, width: '100%', height: '100%' }}>
      <div style={{ fontSize: 9, color: '#0E0E23', fontWeight: 800, textAlign: 'center', marginBottom: 10 }}>What clients say</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {[STOCK.team1, STOCK.team2, STOCK.team3].map((img, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 8, padding: 8, boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
            <Stars />
            <div style={{ fontSize: 6, color: '#475569', marginTop: 4, lineHeight: 1.3 }}>&ldquo;Genuinely changed how we do business.&rdquo;</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
              <img src={img} alt="" style={{ width: 14, height: 14, borderRadius: 999, objectFit: 'cover' }}/>
              <div>
                <div style={{ fontSize: 6, fontWeight: 800, color: '#0E0E23' }}>Sarah K.</div>
                <div style={{ fontSize: 5, color: '#94A3B8' }}>Homeowner</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryGrid() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, width: '100%', height: '100%', padding: 6, background: '#fff' }}>
      {[STOCK.port1, STOCK.port2, STOCK.port3, STOCK.port4].map((img, i) => (
        <img key={i} src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
      ))}
    </div>
  );
}

function TeamRow() {
  return (
    <div style={{ background: '#fff', padding: 14, width: '100%', height: '100%' }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#0E0E23', textAlign: 'center', marginBottom: 10 }}>Meet the team</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {[STOCK.team1, STOCK.team2, STOCK.team3].map((img, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <img src={img} alt="" style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 999 }} />
            <div style={{ fontSize: 7, fontWeight: 800, color: '#0E0E23', marginTop: 4 }}>Sarah K.</div>
            <div style={{ fontSize: 6, color: '#94A3B8' }}>Founder</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactPreview() {
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', background: '#fff' }}>
      <div style={{ flex: 1, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#0E0E23', lineHeight: 1.15 }}>Let&rsquo;s talk</div>
        <div style={{ fontSize: 6, color: '#94A3B8', marginTop: 3 }}>hello@yourbrand.com</div>
        <div style={{ marginTop: 8, background: '#F1F5F9', borderRadius: 4, height: 9 }} />
        <div style={{ marginTop: 4, background: '#F1F5F9', borderRadius: 4, height: 9 }} />
        <div style={{ marginTop: 4, background: '#F1F5F9', borderRadius: 4, height: 22 }} />
        <div style={{ marginTop: 6, background: '#8B5CF6', color: '#fff', fontSize: 7, textAlign: 'center', borderRadius: 4, padding: '4px 0', fontWeight: 700 }}>SEND MESSAGE</div>
      </div>
      <div style={{ flex: 1, background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 12, height: 12, background: '#8B5CF6', borderRadius: 999, boxShadow: '0 0 0 6px rgba(139,92,246,0.25)' }} />
      </div>
    </div>
  );
}

function FaqPreview() {
  return (
    <div style={{ background: '#fff', padding: 14, width: '100%', height: '100%' }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: '#0E0E23', marginBottom: 8 }}>Frequently asked</div>
      {[0,1,2,3].map(i => (
        <div key={i} style={{ borderBottom: '1px solid #E2E8F0', padding: '7px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Line w="60%" h={3} color="#0E0E23" opacity={0.9} mt={0} />
          <div style={{ fontSize: 11, color: '#94A3B8' }}>＋</div>
        </div>
      ))}
    </div>
  );
}

function FooterPreview() {
  return (
    <div style={{ background: '#0E0E23', color: '#fff', padding: 14, width: '100%', height: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
        {['Brand', 'Product', 'Company', 'Legal'].map((c, i) => (
          <div key={i}>
            <div style={{ fontSize: 7, fontWeight: 800, letterSpacing: 0.5 }}>{c}</div>
            <Line w="70%" h={2} color="#fff" opacity={0.4} mt={6} />
            <Line w="60%" h={2} color="#fff" opacity={0.4} mt={3} />
            <Line w="50%" h={2} color="#fff" opacity={0.4} mt={3} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 6, opacity: 0.6 }}>© 2026 Your Brand. All rights reserved.</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['𝕏', '◯', 'in'].map((s, i) => (
            <div key={i} style={{ width: 12, height: 12, borderRadius: 999, background: 'rgba(255,255,255,0.12)', fontSize: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DonationsPreview() {
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <img src={STOCK.donate} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(14,14,35,0.35)' }} />
        <div style={{ position: 'absolute', left: 12, bottom: 12, color: '#fff' }}>
          <div style={{ fontSize: 7, opacity: 0.8, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>Our Mission</div>
          <div style={{ fontSize: 12, fontWeight: 800, marginTop: 2, lineHeight: 1.15 }}>Help us reach 1,000 families</div>
        </div>
      </div>
      <div style={{ flex: 1, background: '#fff', padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: 6, fontWeight: 800, color: '#8B5CF6', letterSpacing: 0.8, textTransform: 'uppercase' }}>Donate</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginTop: 6 }}>
          {['$25','$50','$100'].map((v, i) => (
            <div key={i} style={{ border: '1px solid #E2E8F0', borderRadius: 4, padding: '5px 0', fontSize: 8, fontWeight: 800, textAlign: 'center', background: i === 1 ? '#0E0E23' : '#fff', color: i === 1 ? '#fff' : '#0E0E23' }}>{v}</div>
          ))}
        </div>
        <div style={{ marginTop: 6, background: '#8B5CF6', color: '#fff', fontSize: 8, textAlign: 'center', borderRadius: 4, padding: '6px 0', fontWeight: 800 }}>Donate Now</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 8 }}>
          <div><div style={{ fontSize: 12, fontWeight: 900, color: '#0E0E23' }}>$120k</div><div style={{ fontSize: 6, color: '#94A3B8' }}>raised</div></div>
          <div><div style={{ fontSize: 12, fontWeight: 900, color: '#0E0E23' }}>847</div><div style={{ fontSize: 6, color: '#94A3B8' }}>donors</div></div>
        </div>
      </div>
    </div>
  );
}

function AgencyLongScrollPreview() {
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1.2, position: 'relative' }}>
        <img src={STOCK.service} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(11,15,26,0.85) 0%, rgba(11,15,26,0.6) 100%)' }} />
        <div style={{ position: 'absolute', left: 12, top: 10, color: '#fff' }}>
          <div style={{ fontSize: 6, color: '#F97316', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>Trusted Since 2003</div>
          <div style={{ fontSize: 11, fontWeight: 800, marginTop: 3, lineHeight: 1.15 }}>Local experts, 24/7</div>
          <div style={{ marginTop: 5, display: 'flex', gap: 3 }}>
            <div style={{ background: '#F97316', fontSize: 6, padding: '3px 6px', borderRadius: 999, fontWeight: 800 }}>Call Now</div>
            <div style={{ background: 'rgba(255,255,255,0.15)', fontSize: 6, padding: '3px 6px', borderRadius: 999, fontWeight: 800 }}>Quote</div>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, background: '#0B0F1A', padding: 8, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, alignItems: 'center' }}>
        {['20+', '15K+', '4.9★', '60m'].map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: '#F97316' }}>{s}</div>
            <div style={{ fontSize: 5, color: '#fff', opacity: 0.6 }}>Stat</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductGridPreview() {
  return (
    <div style={{ background: '#FAFAF9', padding: 10, width: '100%', height: '100%' }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#0E0E23', marginBottom: 8 }}>Featured products</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {[STOCK.port1, STOCK.port2, STOCK.port3].map((img, i) => (
          <div key={i}>
            <img src={img} alt="" style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 4 }} />
            <div style={{ fontSize: 6, color: '#0E0E23', fontWeight: 700, marginTop: 3 }}>Product {i+1}</div>
            <div style={{ fontSize: 6, color: '#94A3B8' }}>$49</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Top-level — pick the right shape based on category + first block.type
// ──────────────────────────────────────────────────────────────────────
function shapeFor(categoryId, blocks = []) {
  const first = (blocks[0] && blocks[0].type) || '';
  const tags = (categoryId + ' ' + first).toLowerCase();
  if (tags.includes('agency_glass_stat'))    return 'glass-hero';
  if (tags.includes('agency_service'))       return 'three-card';
  if (tags.includes('agency_cta_band'))      return 'cta-band';
  if (tags.includes('agency_why_grid'))      return 'three-card';
  if (tags.includes('agency_stat_quad'))     return 'stat-quad';
  if (tags.includes('agency_review'))        return 'review-chip';
  if (categoryId === 'agency')               return 'agency-longscroll';
  if (categoryId === 'introduce')            return 'hero';
  if (categoryId === 'services')             return 'three-card';
  if (categoryId === 'pricing')              return 'pricing';
  if (categoryId === 'testimonials')         return 'testimonials';
  if (categoryId === 'shop')                 return 'product-grid';
  if (categoryId === 'portfolio')            return 'gallery';
  if (categoryId === 'images')               return 'gallery';
  if (categoryId === 'menus')                return 'menu';
  if (categoryId === 'contact')              return 'contact';
  if (categoryId === 'donations')            return 'donation';
  if (categoryId === 'faq')                  return 'faq';
  if (categoryId === 'footer')               return 'footer';
  if (categoryId === 'group-events')         return 'split';
  if (categoryId === 'scheduling')           return 'split';
  if (categoryId === 'content-memberships')  return 'split';
  if (categoryId === 'videos')               return 'gallery';
  if (categoryId === 'forms' || categoryId === 'newsletter') return 'newsletter';
  return 'hero';
}

function CtaBandPreview() {
  return (
    <div style={{ background: '#F97316', width: '100%', height: '100%', padding: 14, color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.2)', borderRadius: 999, filter: 'blur(20px)' }} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
        <div>
          <div style={{ fontSize: 6, opacity: 0.9, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>Emergency hotline</div>
          <div style={{ fontSize: 13, fontWeight: 900, marginTop: 4, lineHeight: 1.1 }}>24-hour service?<br/>We answer.</div>
        </div>
        <div style={{ background: '#fff', color: '#F97316', padding: '8px 14px', borderRadius: 999, fontSize: 9, fontWeight: 900 }}>📞 (555) 200-7777</div>
      </div>
    </div>
  );
}

function StatQuadPreview() {
  return (
    <div style={{ background: '#0B0F1A', padding: 16, width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, width: '100%' }}>
        {[{v:'20+',l:'Years'},{v:'15K+',l:'Jobs'},{v:'4.9★',l:'Rating'},{v:'60m',l:'Response'}].map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#F97316' }}>{s.v}</div>
            <div style={{ fontSize: 6, color: '#fff', opacity: 0.7, marginTop: 2, letterSpacing: 0.6, textTransform: 'uppercase' }}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewChipPreview() {
  return (
    <div style={{ background: '#fff', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #E2E8F0', borderRadius: 999, padding: '6px 12px', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
        <div style={{ width: 16, height: 16, borderRadius: 999, background: '#fff', boxShadow: 'inset 0 0 0 1px #E2E8F0', color: '#4285F4', fontSize: 9, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>G</div>
        <div>
          <div style={{ fontSize: 5, opacity: 0.6, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>Reviewed on Google</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
            <span style={{ fontWeight: 900, fontSize: 8 }}>4.9</span>
            <Stars />
            <span style={{ fontSize: 6, opacity: 0.6 }}>· 432 reviews</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewsletterPreview() {
  return (
    <div style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)', width: '100%', height: '100%', padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <Pill color="#8B5CF6">Newsletter</Pill>
      <div style={{ fontSize: 13, fontWeight: 900, color: '#0E0E23', marginTop: 8, textAlign: 'center', lineHeight: 1.15 }}>Stay in the loop</div>
      <div style={{ fontSize: 7, color: '#475569', marginTop: 4, textAlign: 'center' }}>Get our weekly drops in your inbox</div>
      <div style={{ marginTop: 10, display: 'flex', gap: 4, width: '85%' }}>
        <div style={{ flex: 1, background: '#fff', borderRadius: 4, height: 18, display: 'flex', alignItems: 'center', paddingLeft: 6, fontSize: 6, color: '#94A3B8' }}>you@email.com</div>
        <div style={{ background: '#0E0E23', color: '#fff', fontSize: 7, fontWeight: 800, padding: '0 10px', borderRadius: 4, display: 'flex', alignItems: 'center' }}>JOIN</div>
      </div>
    </div>
  );
}

function MenuPreview() {
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', background: '#FFFBEB' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <img src={STOCK.food} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      <div style={{ flex: 1.2, padding: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 900, color: '#0E0E23' }}>Tonight&rsquo;s Menu</div>
        {[
          {n: 'Wagyu Burger',   p: '$18'},
          {n: 'Tuna Tartare',   p: '$22'},
          {n: 'Truffle Pasta',  p: '$28'},
          {n: 'Crème Brûlée',   p: '$12'},
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <div>
              <div style={{ fontSize: 7, fontWeight: 800, color: '#0E0E23' }}>{item.n}</div>
              <Line w={"45%"} h={1.5} color="#A8A29E" opacity={1} mt={2} />
            </div>
            <div style={{ fontSize: 7, fontWeight: 800, color: '#D97706' }}>{item.p}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Dispatcher
// ──────────────────────────────────────────────────────────────────────
import { INTRO_SHAPE_BY_ID } from './IntroShapes';
import { ABOUT_SHAPE_BY_ID, SERVICES_SHAPE_BY_ID } from './CategoryShapes';
import {
  SHOP_SHAPE_BY_ID, PRICING_SHAPE_BY_ID, TESTIMONIALS_SHAPE_BY_ID,
  CONTACT_SHAPE_BY_ID, FOOTER_SHAPE_BY_ID, FAQ_SHAPE_BY_ID,
  AUTH_SHAPE_BY_ID, DONATIONS_SHAPE_BY_ID, VIDEOS_SHAPE_BY_ID,
  FORMS_SHAPE_BY_ID, NEWSLETTER_SHAPE_BY_ID, CALENDAR_SHAPE_BY_ID,
  MENU_SHAPE_BY_ID, AUDIO_SHAPE_BY_ID, CHARTS_SHAPE_BY_ID,
  SOCIAL_SHAPE_BY_ID, PORTFOLIO_SHAPE_BY_ID, IMAGES_SHAPE_BY_ID,
  GROUP_EVENTS_SHAPE_BY_ID, MEMBERSHIPS_SHAPE_BY_ID, SCHEDULING_SHAPE_BY_ID,
  TEAM_SHAPE_BY_ID, CTA_SHAPE_BY_ID, EVENTS_SHAPE_BY_ID,
} from './MoreShapes';

// Iter 175 — Section Audit. Per-template SHAPE map per category so every
// tile in the picker renders a uniquely-shaped miniature. Lookup order:
// (1) template's _previewShape on this category's map, (2) legacy
// shapeFor() fallback below.
const SHAPE_MAPS_BY_CATEGORY = {
  introduce:             INTRO_SHAPE_BY_ID,
  about:                 ABOUT_SHAPE_BY_ID,
  services:              SERVICES_SHAPE_BY_ID,
  shop:                  SHOP_SHAPE_BY_ID,
  pricing:               PRICING_SHAPE_BY_ID,
  testimonials:          TESTIMONIALS_SHAPE_BY_ID,
  contact:               CONTACT_SHAPE_BY_ID,
  footer:                FOOTER_SHAPE_BY_ID,
  faq:                   FAQ_SHAPE_BY_ID,
  'auth-account':        AUTH_SHAPE_BY_ID,
  donations:             DONATIONS_SHAPE_BY_ID,
  videos:                VIDEOS_SHAPE_BY_ID,
  forms:                 FORMS_SHAPE_BY_ID,
  newsletter:            NEWSLETTER_SHAPE_BY_ID,
  calendar:              CALENDAR_SHAPE_BY_ID,
  menus:                 MENU_SHAPE_BY_ID,
  audio:                 AUDIO_SHAPE_BY_ID,
  charts:                CHARTS_SHAPE_BY_ID,
  social:                SOCIAL_SHAPE_BY_ID,
  portfolio:             PORTFOLIO_SHAPE_BY_ID,
  images:                IMAGES_SHAPE_BY_ID,
  'group-events':        { ...GROUP_EVENTS_SHAPE_BY_ID, ...EVENTS_SHAPE_BY_ID },
  'content-memberships': MEMBERSHIPS_SHAPE_BY_ID,
  scheduling:            SCHEDULING_SHAPE_BY_ID,
  team:                  TEAM_SHAPE_BY_ID,
  cta:                   CTA_SHAPE_BY_ID,
};

export default function RichTilePreview({ tpl, categoryId }) {
  // Iter 175 — Section Audit. Every template should declare its own
  // `_previewShape` for an exact-render miniature. Falls through to the
  // legacy shapeFor() heuristic only when a category has not yet been
  // converted to the shape engine (e.g. agency long-scroll).
  const ps = tpl?._previewShape;
  if (ps) {
    const map = SHAPE_MAPS_BY_CATEGORY[categoryId];
    if (map && map[ps]) { const C = map[ps]; return <C />; }
  }

  const shape = shapeFor(categoryId, tpl.blocks || []);
  const first = (tpl.blocks && tpl.blocks[0]) || {};
  const c = first.content || {};
  switch (shape) {
    case 'hero':              return <HeroPreview img={c.image_url || STOCK.hero} headline={c.title || tpl.label} sub={c.tagline} cta={c.cta || 'Get Started'} />;
    case 'split':             return <SplitImageText img={c.image_url || STOCK.about} headline={c.title || tpl.label} sub={c.tagline} />;
    case 'three-card':        return <ThreeCardRow title={c.title || 'What we do'} />;
    case 'pricing':           return <PricingPreview />;
    case 'testimonials':      return <TestimonialsPreview />;
    case 'gallery':           return <GalleryGrid />;
    case 'product-grid':      return <ProductGridPreview />;
    case 'menu':              return <MenuPreview />;
    case 'team':              return <TeamRow />;
    case 'contact':           return <ContactPreview />;
    case 'faq':               return <FaqPreview />;
    case 'footer':            return <FooterPreview />;
    case 'donation':          return <DonationsPreview />;
    case 'agency-longscroll': return <AgencyLongScrollPreview />;
    case 'glass-hero':        return <HeroPreview img={c.image_url || STOCK.service} headline={c.title || 'Trusted Since 2003'} sub="24/7 emergency service" cta="Call Now" />;
    case 'cta-band':          return <CtaBandPreview />;
    case 'stat-quad':         return <StatQuadPreview />;
    case 'review-chip':       return <ReviewChipPreview />;
    case 'newsletter':        return <NewsletterPreview />;
    default:                  return <HeroPreview />;
  }
}
